import type { TaskOccurrence, TaskPriority, TaskStatus } from '@/types';
import { combineLocalDateAndTime, timeOfDayToMinutes, toLocalDateKey } from './dates';

const TERMINAL_STATUSES: ReadonlySet<TaskStatus> = new Set([
  'completed',
  'missed',
  'skipped',
  'deferred'
]);

const PRIORITY_WEIGHT: Record<TaskPriority, number> = {
  low: 0,
  normal: 1,
  high: 2,
  mandatory: 3
};

export interface DayProgress {
  plannedCount: number;
  completedCount: number;
  missedCount: number;
  mandatoryCount: number;
  completedMandatoryCount: number;
  completionPercent: number;
  progressPercent: number;
}

export interface DayProgressOptions {
  includeBreaks?: boolean;
  includeSleep?: boolean;
}

function isIncludedTask(task: TaskOccurrence, options: DayProgressOptions): boolean {
  if (task.taskType === 'break' && !options.includeBreaks) return false;
  if (task.taskType === 'sleep' && !options.includeSleep) return false;
  return true;
}

function taskProgressRatio(task: TaskOccurrence): number {
  if (task.status === 'completed') return 1;
  if ((task.targetValue ?? 0) > 0) {
    return Math.max(0, Math.min(1, task.currentValue / (task.targetValue ?? 1)));
  }
  return 0;
}

export function calculateDayProgress(
  occurrences: readonly TaskOccurrence[],
  options: DayProgressOptions = {}
): DayProgress {
  const included = occurrences.filter((task) => isIncludedTask(task, options));
  const completed = included.filter((task) => task.status === 'completed');
  const mandatory = included.filter((task) => task.priority === 'mandatory');
  const completedMandatory = mandatory.filter((task) => task.status === 'completed');
  const progress = included.reduce((sum, task) => sum + taskProgressRatio(task), 0);

  return {
    plannedCount: included.length,
    completedCount: completed.length,
    missedCount: included.filter((task) => task.status === 'missed').length,
    mandatoryCount: mandatory.length,
    completedMandatoryCount: completedMandatory.length,
    completionPercent:
      included.length === 0 ? 0 : Math.round((completed.length / included.length) * 100),
    progressPercent: included.length === 0 ? 0 : Math.round((progress / included.length) * 100)
  };
}

export type TaskTimingState = 'active' | 'overdue' | 'upcoming' | 'unscheduled' | 'finished';

export function getTaskTimingState(task: TaskOccurrence, now: Date): TaskTimingState {
  if (TERMINAL_STATUSES.has(task.status)) return 'finished';
  if (task.status === 'active') return 'active';
  if (!task.startTime) return 'unscheduled';

  const startsAt = combineLocalDateAndTime(task.date, task.startTime).getTime();
  const endsAt = task.endTime
    ? combineLocalDateAndTime(task.date, task.endTime).getTime()
    : startsAt + (task.estimatedMinutes ?? 0) * 60_000;
  if (now.getTime() >= startsAt && now.getTime() < endsAt) return 'active';
  if (now.getTime() >= endsAt) return 'overdue';
  return 'upcoming';
}

function compareCandidates(a: TaskOccurrence, b: TaskOccurrence, now: Date): number {
  const stateRank: Record<TaskTimingState, number> = {
    active: 0,
    overdue: 1,
    upcoming: 2,
    unscheduled: 3,
    finished: 4
  };
  const stateDifference =
    stateRank[getTaskTimingState(a, now)] - stateRank[getTaskTimingState(b, now)];
  if (stateDifference !== 0) return stateDifference;

  if (a.startTime && b.startTime) {
    const timeDifference = timeOfDayToMinutes(a.startTime) - timeOfDayToMinutes(b.startTime);
    if (timeDifference !== 0) return timeDifference;
  } else if (a.startTime) {
    return -1;
  } else if (b.startTime) {
    return 1;
  }

  const priorityDifference = PRIORITY_WEIGHT[b.priority] - PRIORITY_WEIGHT[a.priority];
  return priorityDifference || a.sortOrder - b.sortOrder || a.createdAt.localeCompare(b.createdAt);
}

export function selectNextTask(
  occurrences: readonly TaskOccurrence[],
  now: Date = new Date()
): TaskOccurrence | undefined {
  const today = toLocalDateKey(now);
  return occurrences
    .filter(
      (task) =>
        task.date === today &&
        !TERMINAL_STATUSES.has(task.status) &&
        task.taskType !== 'break' &&
        task.taskType !== 'sleep'
    )
    .sort((a, b) => compareCandidates(a, b, now))[0];
}

export const getNextTask = selectNextTask;
